import { useEffect } from "react";
import {
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { DetailsApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import { AccountingEventsGroup } from "../types/events";
import { DigitalBusinessDocument } from "../types/documents";
import { CanonicalBusinessEvent } from "../types/cbes";
import { DetailsResponse, Stage } from "../lusca-search-api/generated-src";
import removeEmpty from "../helpers/removeEmpty";
import { useDeferralId } from "../contexes/DeferralIdContext";
import { getDeferralIds } from "../helpers/cbeSummaryUtils";
import {
  cacheFallbackDetails,
  FallbackResponse,
  getDetailsQueryKey,
} from "../helpers/detailsCacheUtils";

export interface DetailsDataInterface {
  events: (AccountingEventsGroup | DigitalBusinessDocument)[];
  cbes: CanonicalBusinessEvent[];
  fallback?: FallbackResponse;
  auids?: string[];
  cachedFromFallback?: boolean;
}

const processRawData = (data: DetailsResponse): DetailsDataInterface => {
  const events = (data.events || []).map((event) => {
    const cleanEvent = removeEmpty(event);

    if ("accountingEvents" in cleanEvent && typeof cleanEvent.sk === "string") {
      cleanEvent.sk = parseInt(cleanEvent.sk, 10);
    }
    if (
      "documentType" in cleanEvent &&
      typeof cleanEvent.version === "string"
    ) {
      cleanEvent.version = parseInt(cleanEvent.version, 10);
    }

    return "documentType" in cleanEvent
      ? (cleanEvent as DigitalBusinessDocument)
      : (cleanEvent as AccountingEventsGroup);
  });

  const cbes = (data.cbes || []).map((cbe) => {
    const cleanCbe = removeEmpty(cbe);

    if (cleanCbe.lockVersion && typeof cleanCbe.lockVersion === "string") {
      cleanCbe.lockVersion = parseInt(cleanCbe.lockVersion, 10);
    }

    return cleanCbe as CanonicalBusinessEvent;
  });

  return {
    events,
    cbes,
    fallback: data.fallback,
    auids: data.auids,
  };
};

export default function useFetchDetails(
  stage: Stage,
  orderId: string,
  fetchOnMount = false,
  updateDeferralContext = true
): UseQueryResult<DetailsDataInterface, Error> {
  const DetailsApi = DetailsApiFactory();
  const { setDeferralIds } = useDeferralId();
  const queryClient = useQueryClient();

  const query = useQuery<DetailsDataInterface, Error>(
    getDetailsQueryKey(stage, orderId),
    async () => {
      const { data } = await DetailsApi.getDetails(stage, orderId);
      const processed = processRawData(data);

      cacheFallbackDetails(queryClient, stage, processed);

      return processed;
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: fetchOnMount,
    }
  );

  useEffect(() => {
    if (!updateDeferralContext || !query.data?.cbes?.length) {
      return;
    }

    let revenueDeferralId = "";
    let royaltyDeferralId = "";

    query.data.cbes.forEach((cbe) => {
      const deferralIds = getDeferralIds(cbe);
      if (deferralIds.revenueDeferralId) {
        revenueDeferralId = deferralIds.revenueDeferralId;
      }
      if (deferralIds.royaltyDeferralId) {
        royaltyDeferralId = deferralIds.royaltyDeferralId;
      }
    });

    setDeferralIds({
      revenueDeferralId,
      royaltyDeferralId,
    });
  }, [query.data?.cbes, setDeferralIds, updateDeferralContext]);

  return query;
}
